import { array, boolean, InferType, number, object, string } from "yup";
import { ContentSchema } from "../content";
import { PlatformSchema } from "../Platform";
import { SiteSchema } from "../Site";
import { ThematicSchema } from "../Thematic";
import { UserSchema } from "../UserModel";
import moment from "moment-timezone";
import { Frequency, TFrequency } from "../CreateOrders";
import { TUserRole } from "../../transports/user";
import { urlsMetricsSchema } from "../urlsMetrics";
import { boostMetricSchema } from "./BoostMetrics";

export enum OrderType {
    NL = "NL",
    FILLING = "FILLING",
    BOOST = "BOOST",
    SILO = "SILO",
    SELL = "SELL",
    RANK = "RANK",
    LEAD = "LEAD",
    DISC = "DISC",
    AFFI = "AFFI",
}
export type TOrderType = keyof typeof OrderType;

export const OffSiteOrderType = ["LP", ...Object.values(OrderType), "PLANNED"];

export type TOffSiteOrderType = typeof OffSiteOrderType[number];

export const OnSiteOrderType = ["LP", ...Object.values(OrderType)];

export type TOnSiteOrderType = typeof OnSiteOrderType[number];

export const urlRegex =
    /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,8}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;
export enum ContentType {
    HOME = "HOME",
    BLOG = "BLOG",
    LANDING = "LANDING",
    PRODUCT = "PRODUCT",
}
export type TContentType = keyof typeof ContentType;

export enum WritingType {
    WRITE = "WRITE",
    AI_AUTO = "AI_AUTO",
    AI_MANUAL = "AI_MANUAL",
    TRANSLATE = "TRANSLATE",
    EXPIRED = "EXPIRED",
}

export const WordCountList = [800, 1000, 1200, 1400];

export enum DraftWritingType {
    WRITE = "WRITE",
    AI_AUTO = "AI_AUTO",
    AI_MANUAL = "AI_MANUAL",
}

export enum JarvisSuggestionType {
    TITLE = "TITLE",
    PLAN = "PLAN",
}

export const upsertSiloSchema = object({
    keywords: array().of(string().required()).max(10).min(1).required(),
});

export const ManagerStatSchema = object({
    id: number().required(),
    domain: string().required(),
    pbn: string().required(),
    manager: string().notRequired(),
    linker: string(),
    planned: number()
        .required()
        .transform((v1) => {
            return !v1 ? 0 : v1;
        }),
    role: string<TUserRole>().notRequired(),
    linkerRole: string<TUserRole>(),
    managerRole: string<TUserRole>().notRequired(),
    previousMonthOrdered: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    currentMonthOrdered: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    nextMonthOrdered: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    previousMonthDelivered: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    currentMonthDelivered: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    nextMonthDelivered: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    currentMonthPublished: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    nextMonthPublished: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    previousMonthPublished: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    currentMonthProcessed: number().transform((v1) => {
        return parseInt(v1, 10);
    }),
    nextMonthProcessed: number().transform((v1) => {
        return parseInt(v1, 10);
    }),
    previousMonthProcessed: number().transform((v1) => {
        return parseInt(v1, 10);
    }),
    currentMonthWaiting: number().transform((v1) => {
        return parseInt(v1, 10);
    }),
    nextMonthWaiting: number().transform((v1) => {
        return parseInt(v1, 10);
    }),
    previousMonthWaiting: number().transform((v1) => {
        return parseInt(v1, 10);
    }),
});

export const AdminStatSchema = object({
    id: number().required(),
    domain: string().required(),
    pbn: string().required(),
    planned: number()
        .required()
        .transform((v1) => {
            return !v1 ? 0 : v1;
        }),
    previousMonthOrdered: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    previousMonthPublished: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    previousMonthIndexed: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    thisMonthOrdered: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    thisMonthPublished: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    thisMonthIndexed: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
    thisMonthLate: number()
        .required()
        .transform((v1) => {
            return parseInt(v1, 10);
        }),
});

export type ManagerStat = InferType<typeof ManagerStatSchema>;
export type AdminStat = InferType<typeof AdminStatSchema>;
export type TJarvisSuggestionType = keyof typeof JarvisSuggestionType;

export type TWritingType = keyof typeof WritingType;

export interface ISuggestedWords {
    important: string[];
    others: string[];
    entities: string[];
}
export type TSuggestedWords = "IMPORTANT" | "OTHERS" | "ENTITIES";
export interface IAiTitlesAndPlans {
    user: string;
    titles: number;
    plans: number;
}
export interface ITotalOrderWithoutCategories {
    user: string;
    total: number;
}

export const linksSchema = object().shape(
    {
        url: string()
            .ensure()
            .when("anchor", {
                is: (anchor: string) => anchor && anchor !== "",
                then: (schema) =>
                    schema
                        .required("Required if anchor is not empty")
                        .matches(urlRegex, "Enter correct url!"),
            }),
        anchor: string()
            .ensure()
            .when("url", {
                is: (url: string) => {
                    return url && url !== "";
                },
                then: (schema) => schema.required("Required if url is not empty"),
            }),
    },
    [["url", "anchor"]]
);

export const jarvisSuggestionSchema = object({
    id: number().required(),
    suggestion: array(string()).transform((v1) => {
        return typeof v1 === "string" ? [v1] : v1;
    }),
    type: string<TJarvisSuggestionType>(),
});

export const orderMetricSchema = object({
    tf: number().nullable(),
    rd: number().nullable(),
    kw: number().nullable(),
    traffic: number().nullable(),
});

//order schema From the DB
export const OrderSchema = object({
    id: number().required(),
    active: boolean().required("error.order.active.required"),
    wordCount: number().required("error.order.wordCount.required").nonNullable(),
    orderPrice: number().required("error.order.orderPrice.required"),
    thematic: ThematicSchema.required("error.order.thematic.required"),
    indexed: number<0 | 1 | 2 | 3>().required(),
    indexChecked: number().nonNullable(),
    platform: PlatformSchema.default(null).nullable(),
    moneySite: SiteSchema.nullable(),
    manager: UserSchema.nullable().default(null),
    writer: UserSchema.nullable().default(null),
    writerNeeded: boolean().default(true),
    proofreader: UserSchema.nullable().default(null),
    guideId: number().nullable(),
    urlsMetrics: array().of(urlsMetricsSchema).notRequired(),
    orderType: string<TOrderType>().required("error.order.orderType.required"),
    boostMetrics: boostMetricSchema.notRequired(),
    orderMetric: orderMetricSchema.notRequired(),
    content: ContentSchema.notRequired(),
    priority: boolean().default(false),
    commentForWriter: string().nullable(),
    commentForLinker: string().nullable(),
    rate: number().nullable(),
    returnCount: number().nullable(),
    rd: SiteSchema.nullable().default(null),
    jarvisSuggestion: array(jarvisSuggestionSchema).nullable(),
    contentType: string<TContentType>()
        .required("error.order.contentType.required")
        .default("BLOG"),
    links: array().of(linksSchema).nullable().default(undefined),
    opti: boolean().default(false),
    approveNeeded: boolean().default(false),
    isApproved: boolean().default(false),
    keywords: array()
        .of(string().required())
        .when("opti", {
            is: (opti: boolean) => opti === true,
            then: (schema) =>
                schema.required("error.order.keywords.required").min(1, "error.order.keywords.min"),

            otherwise: (schema) => schema.nullable(),
        }),
    url: string().nullable(),
    draft: boolean().default(false),
    writerPrice: number().nullable(),
    proofreaderPrice: number().nullable(),
    reviewNeeded: boolean().default(true),
    commentFromProofreader: string().nullable(),
    orderedAt: string().transform((v1) => {
        return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
    }),
    deliveredAt: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
    reviewedAt: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;

            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
    processedAt: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
    sentAt: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
    publishedAt: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
    updatedAt: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
    createdAt: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
    indexedAt: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
});

//response of the Api when we add DraftOrders
export const draftOrderSchema = array(OrderSchema);

//Schema to add a single DraftOrder
export const UpsertDraftOrderDTOSchema = OrderSchema.pick([
    "commentForWriter",
    "commentForLinker",
    "draft",
    "contentType",
    "writerNeeded",
    "orderType",
    "links",
    "keywords",
    "reviewNeeded",
    "opti",
    "approveNeeded",
    "priority",
]).concat(
    object({
        thematicId: number().required("error.order.thematicId.required"),
        moneySiteId: number().required("error.order.moneySiteId.required"),
        orderPrice: number()
            .default(0)
            .required("error.order.orderPrice.required")
            .max(300, "error.order.orderPrice.max"),
        writingType: string<TWritingType>()
            .default("WRITE")
            .required("error.order.writingType.required"),
        title: string().nullable(),
        wordCount: number()
            .when("writerNeeded", {
                is: (writerNeeded: boolean) => writerNeeded,
                then: (schema) =>
                    schema
                        .min(300, "error.order.wordCount.min")
                        .required("error.order.wordCount.required"),
            })
            .default(1200),
        managerId: number().nullable(),
        rdId: number().when("contentType", {
            is: (contentType: TContentType) => contentType === "LANDING",
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
        }),
        delete: boolean().nullable(),
        publishedAt: string()
            .when("priority", {
                is: (priority: boolean) => !priority,
                then: (schema) => schema.required("error.order.publishedAt.required"),
                otherwise: (schema) => schema.nullable(),
            })
            .default(moment().add(7, "d").tz("Europe/Paris").format("YYYY-MM-DD")),
    })
);

//Schema to add a array of draftOrders
export const UpsertDraftOrdersDTOSchema = object({
    data: array()
        .of(UpsertDraftOrderDTOSchema.required())
        .defined()
        .min(1, "Need at least dataSet"),
});

//Schema to add a single DraftOrder
export const UpsertDraftOrderBusinessDTOSchema = UpsertDraftOrderDTOSchema.omit([
    "publishedAt",
    "orderPrice",
    "wordCount",
]).concat(
    object({
        platformId: number().required("error.content.platform.required"),
        orderPrice: number()
            .required("error.order.orderPrice.required")
            .max(300, "error.order.orderPrice.max"),
        wordCount: number().when("writerNeeded", {
            is: (writerNeeded: boolean) => writerNeeded,
            then: (schema) =>
                schema
                    .min(300, "error.order.wordCount.min")
                    .required("error.order.wordCount.required"),
        }),
        orderType: string<TOrderType>().default(OrderType.SELL),
    })
);

export const UpsertDraftOrdersBusinessDTOSchema = object({
    data: array().of(UpsertDraftOrderBusinessDTOSchema).defined().min(1, "Need at least dataSet"),
});

//Schema to edit a no draft order
export const UpsertOrderDTOSchema = OrderSchema.pick([
    "commentForWriter",
    "commentForLinker",
    "rate",
    "writerNeeded",
    "wordCount",
    "returnCount",
    "contentType",
    "links",
    "keywords",
    "url",
    "orderPrice",
    "writerPrice",
    "proofreaderPrice",
    "reviewNeeded",
    "opti",
    "priority",
    "createdAt",
    "updatedAt",
    "approveNeeded",
    "isApproved",
    "deliveredAt",
    "reviewedAt",
    "processedAt",
    "orderType",
    "sentAt",
    "publishedAt",
]).concat(
    object({
        id: number(),
        managerId: number(),
        addSendAt: boolean().nullable(),
        writerId: number().nullable(),
        writingType: string<TWritingType>().required("error.order.writingType.required"),
        title: string().nullable(),
        proofreaderId: number().nullable(),
        contentId: number().nullable(),
        platformId: number().nullable(),
        thematicId: number().nullable(),
        moneySiteId: number().nullable(),
        rdId: number().when("contentType", {
            is: (contentType: TContentType) => contentType === "LANDING",
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
        }),
    })
);

export const upsertUrlDTOSchema = UpsertOrderDTOSchema.omit(["url", "orderPrice"]).concat(
    object({
        url: string()
            .matches(urlRegex, "error.order.url.matches")
            .required("error.order.url.required"),
        orderPrice: number().required("error.order.orderPrice.required"),
        publishedAt: string().required("error.order.publishedAt.required"),
    })
);

export const UpsertAIOrderTextStep1Schema = object({
    title: string().required(),
});

export const UpsertOnSiteEditSchema = object({
    title: string().required(),
    metaTitle: string().min(35).max(80).required(),
    metaDescription: string().min(120).max(180).required(),
});

export const UpsertAIOrderTextStep2Schema = object({
    commentForWriter: string().required(),
});

export const upsertPublishOnSchema = object({
    moneySiteId: number().required("error.order.moneySite.required"),
    wordCount: number().default(1000),
    frequency: string<TFrequency>().default(Frequency.EVERY_3_DAYS),
});

export const addMissingOrderSchema = object({
    moneySiteId: number().required(),
    rdId: number().required(),
    wordCount: number().required().default(1000),
    orderType: string<TOrderType>().default(OrderType.FILLING),
    writingType: string<TWritingType>().default(WritingType.WRITE),
    contentType: string<TContentType>().default(ContentType.BLOG),
    publishedAt: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
    title: string().required(),
    url: string().required(),
    links: array().of(linksSchema).notRequired().default(undefined),
    keywords: array().notRequired(),
    metaTitle: string().required(),
    metaDescription: string().required(),
});

export type links = InferType<typeof linksSchema>;
export type upsertUrlDTO = InferType<typeof upsertUrlDTOSchema>;
export type draftOrder = InferType<typeof draftOrderSchema>;
export type UpsertDraftOrderDTO = InferType<typeof UpsertDraftOrderDTOSchema>;
export type UpsertDraftOrdersDTO = InferType<typeof UpsertDraftOrdersDTOSchema>;
export type UpsertDraftOrderBusinessDTO = InferType<typeof UpsertDraftOrderBusinessDTOSchema>;
export type UpsertDraftOrdersBusinessDTO = InferType<typeof UpsertDraftOrdersBusinessDTOSchema>;
export type UpsertOrderDTO = InferType<typeof UpsertOrderDTOSchema>;
export type Order = InferType<typeof OrderSchema>;
export type UpsertPublishOn = InferType<typeof upsertPublishOnSchema>;
export type upsertSilo = InferType<typeof upsertSiloSchema>;
export type addMissingOrder = InferType<typeof addMissingOrderSchema>;

export interface INetlinkingOrderInfo {
    date: string;
    keyword: string;
    anchor: string;
}
